import Vue from "vue";
import Vuex from "vuex";
import { $api, setProp, deleteProp } from "bh-mod";
import moment from "moment";

Vue.use(Vuex);

export default {
	state: {
		crumbs: "Email",
		allSettings: {},
		getStarted: false,
		commissionPayouts: [],
		loading: false,
		maxLeadScore: null,
		forms: [],
		leadTags: [],
		taskTypes: [],
		users: [],
	},
	mutations: {
		SET_COMMISSION_PAYOUTS(state, data) {
			state.commissionPayouts = data;
		},
		UPDATE_CRUMBS(state, data) {
			state.crumbs = data;
		},
		SET_SETTINGS(state, data) {
			let temp = data;

			temp.app = temp.app || { options: {} };
			temp.app.options.blocklistAlertUsers = temp.app.options.blocklistAlertUsers || [];

			state.allSettings = temp;
		},
		SET_PROP: (state, { where, what, del = false }) => {
			if (del) return deleteProp(state, where);

			if (what.status === null) what.status = "bh_lead";
			else if (typeof what.status === "object") what.status = what.status.id;

			if (what.tags && what.tags.length) {
				what.tags = what.tags.map((tag) => {
					if (typeof tag == "object") return tag.id;
					else return tag;
				});
			}
			state.newTime = Date.now();
			setProp(state, where, what);
		},
		SET_APPDATA(state, { maxLeadScore = 0 }) {
			state.maxLeadScore = maxLeadScore
		},
		GET_STARTED: (state) => {
			state.getStarted = true;
		},
		LOADING: (state, bool = !state.loading) => state.loading = bool,
		SET_FORMS(state, data) {
			data.sort((a, b) => moment(b.updatedAt).format('x') - moment(a.updatedAt).format('x'))
			state.forms = data || [];
		},
		SET_TASK_TYPES(state, data) {
			state.taskTypes = data || [];
		},
		SET_LEAD_TAGS(state, data) {
			state.leadTags = data || [];
		},
		SET_USERS(state, data) {
			state.users = data;
		},
	},
	actions: {
		GET_TIMELINES: async ({ commit }) => {
			commit("LOAD", true);
			$api
				.get("/timelines/:instance/ipm")
				.then((res) => {
					commit("SET_TIMELINES", res.data);
				})
				.catch((err) => {
					console.log(err);
				})
				.finally(() => {
					commit("LOAD", false);
				});
		},
		SET_APPDATA: async ({ commit, dispatch }, data) => {
			commit("SET_APPDATA", data);

			dispatch('FETCH_FORMS');
			dispatch('FETCH_LEAD_TAGS');
			dispatch('FETCH_CRM_APP_SETTINGS');
			dispatch('GET_USERS');
		},
		CLOSE_FORM_DRAWER: ({ commit }, { type, data }) => {
			if (type !== "click")
				commit("SET_PROP", {
					where: ["allForms", data.id],
					what: data,
					del: type === "delete",
				});
			commit("CLOSE_DRAWERS");
		},
		GET_COMMISSION_PAYOUTS({ commit }) {
			return new Promise((resolve, reject) => {
				$api
					.get(`/transactions/:instance/commission-payouts`)
					.then(({ data }) => {
						if (data.length) {
							commit("SET_COMMISSION_PAYOUTS", data);
						}
						return resolve(data);
					})
					.catch((err) => {
						reject(err);
					});
			});
		},
		async FETCH_FORMS({ commit, rootState }) {
			try {
				const { data } = await $api.get(`/forms/:instance?type_ne=worksheet&includeParent=true`);
				const forms = data.map(form => {
					if (form.instanceData && form.instanceData.id !== rootState.instance.id) {
						const projectType = Vue.prototype.$capitalize(form.instanceData.projectType)
						form.label = form.name + ` (${projectType}:${form.instanceData.name})`
					} else {
						form.label = form.name
					}
					return form
				})
				commit('SET_FORMS', forms)
			} catch (error) {
				console.error('Error occurred while fetching form list', error)
			}
		},

		async FETCH_CRM_APP_SETTINGS({ commit }) {
			try {
				const { data } = await $api.get(`/settings/:instance/contacts`);

				const taskTypes = data?.options?.taskType?.length ? data.options.taskType : [
					{ name: 'To-do', id: 'todo' },
					{ name: 'Call', id: 'call' },
					{ name: 'Follow up', id: 'followup' }
				];
				commit('SET_TASK_TYPES', taskTypes)
			} catch (error) {
				console.error('Error occurred while fetching crm task types', error)
			}
		},

		async FETCH_LEAD_TAGS({ commit }) {
			try {
				const { data } = await $api.get(`/tags/:instance?type=contacts`);
				commit('SET_LEAD_TAGS', data)
			} catch (error) {
				console.error('Error occurred while fetching lead tags', error)
			}
		},

		GET_USERS: async ({ commit }) => {
			try {
				const { data } = await $api.get(`/instances/:instance/users`);
				commit('SET_USERS', data.users || []);
			} catch (error) {
				console.log(error, 'Error while fetching users');
			}
		}
	},
	getters: {
		isWorksheetActive(state, getters, rootState, rootGetters) {
			return rootGetters.hasWorksheetAppAccess && (state.allSettings?.instance?.options?.activeFeatures?.worksheet ?? true);
		},

		sentimentsObj: state => {
			return state.allSettings?.app?.options?.sentimentOptions?.reduce((acc, curr) => {
				acc[curr.id] = curr.name;
				return acc;
			}, {}) || {};
		},

		csvTaskRecords(state, getters, rootState) {
			const taskTypesObj = state.allSettings?.app?.options?.taskType || [];
			const records = rootState.records.tasks || [];
			return records.map((task) => {
				return {
					id: task.id,
					title: task.title,
					description: task.description,
					sentiment: task.sentiment,
					dueDate: task.dueDate ? Vue.prototype.$formatDate(task.dueDate, 'lll').replaceAll(", ", " ") : "",
					type: task.type ? taskTypesObj[task.type] : "",
					priority: task.priority ? task.priority.toUpperCase() : "",
					contact: task.contact ? `${task.contact.firstName} ${task.contact.lastName} (${task.contact.email})` : "",
					opportunity: task.opportunity ? task.opportunity.name : "",
					assignTo: task.assignTo ? task.assignTo.map((user) => `${user.firstName} ${user.lastName} (${user.email})`).join(", ") : "",
					comments: task.comments ? task.comments.map((comment) => `${comment.content}`).join("\n\n ") : "",
					createdBy: task.createdBy ? `${task.createdBy.firstName} ${task.createdBy.lastName} (${task.createdBy.email})` : "",
					createdDate: task.createdAt ? Vue.prototype.$formatDate(task.createdAt, 'lll').replaceAll(", ", " ") : "",
				}
			})
		}
	},
};
